import React from 'react';
import { Select } from 'antd';
import {
  useFilterLabelClassName,
  useFilterContainerClassName,
  useFilterInputContainerClassName
} from '../../utils/languageUtils';
import { useQuery } from '@apollo/client';
import { GET_HUB_LIST, GetHubListResponse } from '../../pages/wms/bags/graphql/queries/getHubList';

interface HubSelectFilterProps {
  label: string;
  value?: string;
  onChange: (value: string | undefined, hubId?: string | undefined) => void; // 同时传递 value 和 hubId
  onSelectHub?: (hubId: string | undefined) => void; // 新增：选择时返回 hub ID（保留兼容性）
  spanClassName?: string;
  placeholder?: string;
  className?: string;
}

/**
 * HUB选择过滤器组件
 * 用于1st Sort Code的下拉选择，从HUB列表中选择
 */
export const HubSelectFilter: React.FC<HubSelectFilterProps> = ({
  label,
  value,
  onChange,
  onSelectHub,
  spanClassName,
  placeholder = 'Select HUB',
  className
}) => {
  const { data, loading } = useQuery<GetHubListResponse>(GET_HUB_LIST);

  // 构建下拉选项
  const hubOptions = React.useMemo(() => {
    if (!data?.hubList) return [];

    return data.hubList
      .filter(hub => hub.siteType === 1 && hub.sortingCode) // 仅 HUB(siteType=1)，并过滤无分拣码
      .map(hub => {
        // 提取一段码（sorting code的第一段）
        const firstSegment = hub.sortingCode?.split('-')[0] || '';
        return {
          label: `${hub.name} (${firstSegment})`,
          value: firstSegment,
          hubId: hub.id, // 新增：保存 hub ID，用于级联查询
          // 用于搜索的文本，包含名称和代码
          searchText: `${hub.name} ${firstSegment} ${hub.sortingCode}`
        };
      })
      .filter(opt => opt.value); // 过滤空值
  }, [data]);

  // 当 value 为 undefined 时，使用 null 来确保 Select 组件清空显示
  // Ant Design Select 组件对 null 的处理更可靠
  const selectValue = value === '' || value === null || value === undefined ? null : value;

  // 使用统一的样式 Hook，根据语言自动返回合适的样式类名
  const containerClassName = useFilterContainerClassName(className);
  const labelClassName = useFilterLabelClassName(spanClassName);
  const inputContainerClassName = useFilterInputContainerClassName();

  return (
    <div className={containerClassName}>
      <span className={labelClassName}>{label}</span>
      <div className={inputContainerClassName}>
        <Select
          showSearch
          allowClear
          placeholder={placeholder}
          value={selectValue}
          onChange={(val, option) => {
            const nextVal = (val as string) || undefined;
            const hubId = option ? ((option as any)?.hubId as string | undefined) : undefined;
            // 同时传递 value 和 hubId，让父组件一次性更新状态
            onChange(nextVal, nextVal ? hubId : undefined);
            // 保留 onSelectHub 回调以兼容旧代码
            if (onSelectHub) {
              onSelectHub(nextVal ? hubId : undefined);
            }
          }}
          loading={loading}
          options={hubOptions}
          filterOption={(input, option) => {
            const searchText = (option as any)?.searchText || '';
            return searchText.toLowerCase().includes(input.toLowerCase());
          }}
          className="w-full"
          size="middle"
        />
      </div>
    </div>
  );
};

